/** Chhattisgarh cities shown when missing from API (no numeric id until DB seed). */
const DEFAULT_CG_CITY_NAMES = [
  "Raipur",
  "Bhilai",
  "Durg",
  "Bilaspur",
  "Korba",
];

export function isNumericMasterId(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

export function isSyntheticCityId(id) {
  if (id == null || id === "") return false;
  return !isNumericMasterId(id);
}

/** Ensure Raipur, Bhilai, Durg appear in dropdowns even before DB seed. */
export function mergeCityOptions(apiCities = []) {
  const list = Array.isArray(apiCities) ? [...apiCities] : [];
  const names = new Set(
    list.map((c) => String(c.name || "").trim().toLowerCase()).filter(Boolean),
  );

  for (const name of DEFAULT_CG_CITY_NAMES) {
    const key = name.toLowerCase();
    if (!names.has(key)) {
      list.push({ id: `cg-${key}`, name, _synthetic: true });
      names.add(key);
    }
  }

  return list.sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "en", {
      sensitivity: "base",
    }),
  );
}

/**
 * create_profile needs numeric LocationCityID OR text PaitthiNivasKhor.
 * Synthetic cg-* ids must not be sent as LocationCityID (causes 500 on server).
 */
export function resolveCityFieldsForSubmit(cityID, cities = []) {
  const row = cities.find((c) => String(c.id) === String(cityID));
  const name = row?.name ? String(row.name).trim() : "";

  if (isNumericMasterId(cityID)) {
    return { LocationCityID: Number(cityID) };
  }

  if (name) {
    return { PaitthiNivasKhor: name };
  }

  return {};
}

export function parseMasterId(value) {
  if (!isNumericMasterId(value)) return undefined;
  return Number(value);
}

/** Strip values that cause MySQL 500 on create_profile (invalid FK / cg-* city ids / 0). */
export function sanitizeCreateProfilePayload(data) {
  const out = { ...data };

  if (out.LocationCityID != null) {
    if (isSyntheticCityId(out.LocationCityID)) {
      delete out.LocationCityID;
    } else {
      const cityNum = Number(out.LocationCityID);
      if (!Number.isFinite(cityNum) || cityNum <= 0) {
        delete out.LocationCityID;
      } else {
        out.LocationCityID = cityNum;
      }
    }
  }

  for (const key of [
    "Gender",
    "GenderID",
    "MaritalStatusID",
    "GotraID",
    "NanaGotraId",
    "HeightID",
    "Weight",
  ]) {
    if (out[key] == null || out[key] === "") {
      delete out[key];
      continue;
    }
    const n = Number(out[key]);
    if (!Number.isFinite(n) || n <= 0) {
      delete out[key];
    } else {
      out[key] = n;
    }
  }

  return out;
}

export const SIGNUP_WIZARD_STORAGE_KEY = "dw_signup_wizard";

export function saveSignupWizardCredentials(userId, password) {
  try {
    sessionStorage.setItem(
      SIGNUP_WIZARD_STORAGE_KEY,
      JSON.stringify({
        userId: String(userId || ""),
        password: String(password || ""),
        at: Date.now(),
      }),
    );
  } catch {
    /* ignore quota */
  }
}

export function readSignupWizardCredentials() {
  try {
    const raw = sessionStorage.getItem(SIGNUP_WIZARD_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.userId) return null;
    return data;
  } catch {
    return null;
  }
}

export function clearSignupWizardCredentials() {
  try {
    sessionStorage.removeItem(SIGNUP_WIZARD_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
